// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import EventAddress from "@store/Library/Components/Events/Common/EventAddress";
import EventDate from "@store/Library/Components/Events/Common/EventDate";
import EventImage from "@store/Library/Components/Events/Common/EventImage";
import EventTitle from "@store/Library/Components/Events/Common/EventTitle";
import Info from "@store/Library/Components/Icons/Info";
import { linkStyles } from "@store/Themes/common/mixins/link";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    showAddToCal = "showAddToCal",
}

interface Props extends WidgetProps {
    fields: {
        [fields.showAddToCal]: boolean;
    };
}

export interface EventDetailsHeaderStyles {
    container?: GridContainerProps;
    imageGridItem?: GridItemProps;
    contentGridItem?: GridItemProps;
    infoWrapper?: InjectableCss;
    infoIcon?: IconPresentationProps;
    infoTextWrapper?: InjectableCss;
    infoTitle?: TypographyPresentationProps;
    infoText?: TypographyPresentationProps;
    infoLink?: LinkPresentationProps;
}

export const eventDetailsHeaderStyles: EventDetailsHeaderStyles = {
    container: {
        gap: 0,
    },
    imageGridItem: {
        width: [12, 12, 6, 7, 7],
    },
    contentGridItem: {
        width: [12, 12, 6, 5, 5],
        css: css`
            flex-direction: column;
            background-color: ${getColor("common.accent")};

            ${({ theme }) => css`
                ${breakpointValues(theme.design.layout.spacing.content, ["padding"])}
            `}
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-wrap: nowrap;
            margin-top: ${pxToRem(15)};
        `,
    },
    infoIcon: {
        color: "primary",
        css: css`
            margin-right: ${pxToRem(10)};
        `,
    },
    infoTextWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    infoTitle: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.semibold};
        `,
    },
    infoText: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.base};
        `,
    },
    infoLink: {
        css: css`
            ${linkStyles("primary.main", "primary.dark", false, true)}
            font-weight: ${x => x.theme.design.typography.weight.semibold};
        `,
    },
};

const styles = eventDetailsHeaderStyles;

const EventDetailsHeader: FC<Props> = ({ fields }) => {
    const page = useCurrentPage();

    if (!page) {
        return null;
    }

    const {
        fields: {
            title,
            startDate,
            endDate,
            hideHourMinutes,
            dateTimezone,
            isLocalBranch,
            localBranchTitle,
            addressWrapper,
            previewImage,
            eventSummary,
            addInfoTitle,
            addInfoPhone,
            addInfoEmail,
        },
    } = page;

    return (
        <GridContainer {...styles.container} key={page.id}>
            <GridItem {...styles.imageGridItem}>
                <EventImage previewImage={previewImage} altText={title} aspectRatio="16/9" />
            </GridItem>
            <GridItem {...styles.contentGridItem}>
                <EventTitle title={title} variant="h2" forwardAs="h1" />
                <EventDate
                    startDate={startDate}
                    endDate={endDate}
                    hideHourMinutes={hideHourMinutes}
                    dateTimezone={dateTimezone}
                    title={title}
                    eventSummary={eventSummary}
                    isLocalBranch={isLocalBranch}
                    addressWrapper={addressWrapper}
                    showTitle
                    showAddToCal={fields.showAddToCal}
                />
                <EventAddress
                    addressWrapper={addressWrapper}
                    isLocalBranch={isLocalBranch}
                    localBranchTitle={localBranchTitle}
                    showTitle
                    showLink
                />
                {addInfoTitle && (addInfoPhone || addInfoEmail) && (
                    <StyledWrapper {...styles.infoWrapper}>
                        <Icon src={Info} {...styles.infoIcon} />
                        <StyledWrapper {...styles.infoTextWrapper}>
                            <Typography {...styles.infoTitle}>{addInfoTitle}</Typography>
                            {addInfoPhone && (
                                <>
                                    <Hidden above="sm">
                                        <Link href={`tel:${addInfoPhone}`} {...styles.infoLink}>
                                            {addInfoPhone}
                                        </Link>
                                    </Hidden>
                                    <Hidden below="md">
                                        <Typography {...styles.infoText}>{addInfoPhone}</Typography>
                                    </Hidden>
                                </>
                            )}
                            {addInfoEmail && (
                                <Link href={`mailto:${addInfoEmail}`} {...styles.infoLink}>
                                    {addInfoEmail}
                                </Link>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: EventDetailsHeader,
    definition: {
        group: constants.widgetGroup.eventDetails,
        allowedContexts: ["EventDetailsPage"] as any,
        fieldDefinitions: [
            {
                name: fields.showAddToCal,
                displayName: "Show add to calendar",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
